import { z } from 'zod';

import { collectionPath, defineModelSpec, orderBy } from '@elara/firebolt/shared';

import { RouteRelativePathSchema } from './schemas/route-path-schemas.js';
import { id, userId } from './schemas/zod-types.js';

export const NotificationTypeSchema = z.enum(['trip', 'booking', 'account', 'feature']);
export type NotificationType = z.infer<typeof NotificationTypeSchema>;

/**
 * The canonical `ModelSpec` for the user notifications collection.
 * Notifications keep users informed about trip events, booking updates, and account activity.
 * Stored as a per-user subcollection at `users/{userId}/notifications/{id}`.
 */
export const NotificationModelSpec = defineModelSpec({
  path: collectionPath`users/${userId}/notifications/${id}`,
  shape: {
    title: z.string().describe('Notification title'),
    body: z.string().nullish().describe('Optional notification body text'),
    // Route-resolvable in-app path only (e.g. "/trip/123"). Enforced at the write boundary so every
    // stored/forwarded link is one the web router and native deep-link resolver can actually open —
    // an absolute/protocol-relative/other-scheme URL can never reach `navigate()` (ELARA-698/699).
    url: RouteRelativePathSchema.describe('In-app route-relative path to open when the notification is tapped'),
    imageUrl: z.string().nullish().describe('Optional image URL (e.g. room photo, airline logo)'),
    read: z.boolean().default(false).describe('Whether the notification has been read'),
    tripId: z.string().nullish().describe('Optional linked trip ID'),
    type: NotificationTypeSchema.describe('Notification category'),
  },
  constraints: [orderBy('createdAt', 'desc')],
});
export type NotificationModelSpec = typeof NotificationModelSpec;
